import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

const NavigationContext = createContext();

const STORAGE_KEY = "nav-active-panel"; // "", "before", "during", "after"

// Helpers for hash-based panel state (e.g. #before-quick-tips)
function setHashPanel(panel) {
  if (typeof window === "undefined") return;
  window.location.hash = panel ? panel : "";
}

function getHashPanel() {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash || "";
  const value = raw.replace(/^#/, ""); // e.g. "before-quick-tips"
  return value || null;
}

export function NavigationProvider({ children }) {
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  const [checklistsOpen, setChecklistsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(""); // "before" | "during" | "after" | ""
  const [sitRepPanelIndex, setSitRepPanelIndex] = useState(0);
  const [openHub, setOpenHub] = useState(false);
  const [openTimeline, setTimelineOpen] = useState(false);
  const [openHubFlag, setOpenHubFlag] = useState(false);

  // Global weather alert sidebar state
  const [weatherAlertSidebarOpen, setWeatherAlertSidebarOpen] =
    useState(false);
  const [weatherAlertForSidebar, setWeatherAlertForSidebar] =
    useState(null);

  // Before highlights
  const [highlightChecklist, setHighlightChecklist] = useState(false);
  const [highlightRoadmap, setHighlightRoadmap] = useState(false);
  const [highlightQuickTips, setHighlightQuickTips] = useState(false);

  // "During" hub nav highlight
  const [highlightHubNavIndex, setHighlightHubNavIndex] =
    useState(null);

  // Before scroll/centering
  const [roadmapCenterRequested, setRoadmapCenterRequested] =
    useState(false);
  const [beforeScrollRequested, setBeforeScrollRequested] =
    useState(false);
  const [quickTipsCenterRequested, setQuickTipsCenterRequested] =
    useState(false);
  const [lastBeforeTarget, setLastBeforeTarget] = useState(null);

  // During scroll
  const [duringScrollRequested, setDuringScrollRequested] =
    useState(false);

  // After-specific state
  const [highlightAfterTimeline, setHighlightAfterTimeline] =
    useState(false);
  const [highlightAfterTips, setHighlightAfterTips] = useState(false);
  const [highlightAfterChecklists, setHighlightAfterChecklists] =
    useState(false);
  const [lastAfterTarget, setLastAfterTarget] = useState(null);
  const [afterTargetPanelId, setAfterTargetPanelId] = useState(null);
  const [afterScrollRequested, setAfterScrollRequested] =
    useState(false);

  const trackNavigation = useCallback((eventName, params = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        event_category: "Navigation",
        ...params,
      });
    }
    if (process.env.NODE_ENV === "development") {
      console.log("GA event:", eventName, params);
    }
  }, []);

  // Close all three primary panels in a consistent way
  const closeAllPrimaryPanels = useCallback(() => {
    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
  }, []);

  // BEFORE openers

  // Top-level Before, treated as Quick Tips by default
  const openBeforePlain = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setRoadmapOpen(true);
      setActivePanel("before");
      setBeforeScrollRequested(true);
      setLastBeforeTarget("quick-safety-tips-and-tools");
      setHighlightQuickTips(true);
      setQuickTipsCenterRequested(true);
      setHashPanel("before-quick-tips"); // canonical hash for Before default
      trackNavigation("open_before_plain", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // Readiness Roadmap
  const openRoadmap = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setRoadmapOpen(true);
      setActivePanel("before");
      setBeforeScrollRequested(true);
      setRoadmapCenterRequested(true);
      setHighlightRoadmap(true);
      setLastBeforeTarget("readiness-roadmap");
      setHashPanel("before-roadmap");
      trackNavigation("open_roadmap", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // Quick Safety Tips and Tools
  const openBeforeGeneric = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setRoadmapOpen(true);
      setActivePanel("before");
      setBeforeScrollRequested(true);
      setLastBeforeTarget("quick-safety-tips-and-tools");
      setHighlightQuickTips(true);
      setQuickTipsCenterRequested(true);
      setHashPanel("before-quick-tips");
      trackNavigation("open_before_generic", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // Preparedness Checklists
  const openBeforeChecklists = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setRoadmapOpen(true);
      setActivePanel("before");
      setChecklistsOpen(true);
      setHighlightChecklist(true);
      setBeforeScrollRequested(true);
      setLastBeforeTarget("preparedness-checklists");
      setHashPanel("before-checklists");
      trackNavigation("open_before_checklists", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // DURING

  const openEmergencyHub = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setOpenHub(true);
      setActivePanel("during");
      setDuringScrollRequested(true); // flag for IndexPage
      setHashPanel("during");
      trackNavigation("open_hub", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // AFTER

  // Top-level Recovery Timeline section
  const openTimelinePanel = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setTimelineOpen(true);
      setActivePanel("after");
      setLastAfterTarget("recovery-timeline");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);

      setAfterScrollRequested(true); // flag for IndexPage
      setHashPanel("after");
      trackNavigation("open_timeline", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // After: Mental Health
  const openAfterMentalHealth = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setTimelineOpen(true);
      setActivePanel("after");
      setHighlightAfterTips(true);
      setLastAfterTarget("mental-health-resources");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("mental-health-resources");
      }, 0);

      setAfterScrollRequested(true); // also scroll the After card
      setHashPanel("after-mental-health");
      trackNavigation("open_after_mental_health", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // After: Timeline (explicit deep link)
  const openAfterTimeline = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setTimelineOpen(true);
      setActivePanel("after");
      setHighlightAfterTimeline(true);
      setLastAfterTarget("recovery-timeline");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-timeline");
      }, 0);

      setAfterScrollRequested(true);
      setHashPanel("after-timeline");
      trackNavigation("open_after_timeline", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // After: Checklists
  const openAfterChecklists = useCallback(
    (source = "unknown") => {
      closeAllPrimaryPanels();

      setTimelineOpen(true);
      setActivePanel("after");
      setHighlightAfterChecklists(true);
      setLastAfterTarget("recovery-checklists");

      setAfterTargetPanelId(null);
      setTimeout(() => {
        setAfterTargetPanelId("recovery-checklists");
      }, 0);

      setAfterScrollRequested(true);
      setHashPanel("after-checklists");
      trackNavigation("open_after_checklists", { source });
    },
    [closeAllPrimaryPanels, trackNavigation]
  );

  // WEATHER

  const openWeatherAlertSidebar = useCallback(
    (alert, source = "unknown") => {
      setWeatherAlertForSidebar(alert);
      setWeatherAlertSidebarOpen(true);
      trackNavigation("open_weather_alert_sidebar", {
        source,
        event: alert?.properties?.event,
        severity: alert?.properties?.severity,
      });
    },
    [trackNavigation]
  );

  const closeWeatherAlertSidebar = useCallback(
    (source = "unknown") => {
      setWeatherAlertSidebarOpen(false);
      trackNavigation("close_weather_alert_sidebar", { source });
    },
    [trackNavigation]
  );

  const closePanels = useCallback(() => {
    setRoadmapOpen(false);
    setChecklistsOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);
    setActivePanel("");
    setWeatherAlertSidebarOpen(false);
    setWeatherAlertForSidebar(null);
    setHighlightChecklist(false);
    setHighlightRoadmap(false);
    setHighlightQuickTips(false);
    setHighlightHubNavIndex(null);
    setRoadmapCenterRequested(false);
    setBeforeScrollRequested(false);
    setQuickTipsCenterRequested(false);
    setDuringScrollRequested(false);
    setHighlightAfterTips(false);
    setHighlightAfterTimeline(false);
    setHighlightAfterChecklists(false);
    setAfterTargetPanelId(null);
    setAfterScrollRequested(false);
    setHashPanel(null); // clear #...
  }, []);

  // Map current hash to UI state
  const applyHashPanel = useCallback(
    (source = "hashchange") => {
      const hashPanel = getHashPanel();

      // Top-level hashes (before maps to Quick Tips)
      if (hashPanel === "before") {
        openBeforeGeneric(source);
        return;
      }
      if (hashPanel === "during") {
        openEmergencyHub(source);
        return;
      }
      if (hashPanel === "after") {
        openTimelinePanel(source);
        return;
      }

      // Specific Before hashes
      if (hashPanel === "before-roadmap") {
        openRoadmap(source);
        return;
      }
      if (hashPanel === "before-quick-tips") {
        openBeforeGeneric(source);
        return;
      }
      if (hashPanel === "before-checklists") {
        openBeforeChecklists(source);
        return;
      }

      // Specific During hash (alias)
      if (hashPanel === "during-hub") {
        openEmergencyHub(source);
        return;
      }

      // Specific After hashes
      if (hashPanel === "after-timeline-panel") {
        openTimelinePanel(source);
        return;
      }
      if (hashPanel === "after-timeline") {
        openAfterTimeline(source);
        return;
      }
      if (hashPanel === "after-mental-health") {
        openAfterMentalHealth(source);
        return;
      }
      if (hashPanel === "after-checklists") {
        openAfterChecklists(source);
        return;
      }

      // Unknown or empty hash: no-op
    },
    [
      openBeforeGeneric,
      openEmergencyHub,
      openTimelinePanel,
      openRoadmap,
      openBeforeChecklists,
      openAfterTimeline,
      openAfterMentalHealth,
      openAfterChecklists,
    ]
  );

  // On first mount: honor hash deep-link, else fall back to localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hashPanel = getHashPanel();

    if (hashPanel) {
      applyHashPanel("deep_link");
      return;
    }

    const saved = window.localStorage.getItem(STORAGE_KEY) || "";

    setRoadmapOpen(false);
    setOpenHub(false);
    setTimelineOpen(false);

    setActivePanel(saved);

    if (saved === "before") {
      setRoadmapOpen(true);
    } else if (saved === "during") {
      setOpenHub(true);
    } else if (saved === "after") {
      setTimelineOpen(true);
    }
  }, [applyHashPanel]);

  // Listen for hashchange
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleHashChange = () => {
      applyHashPanel("hashchange");
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [applyHashPanel]);

  // Persist activePanel
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, activePanel);
  }, [activePanel]);

  const value = {
    roadmapOpen,
    setRoadmapOpen,
    checklistsOpen,
    setChecklistsOpen,
    activePanel,
    setActivePanel,
    sitRepPanelIndex,
    setSitRepPanelIndex,
    openHub,
    setOpenHub,
    openTimeline,
    setTimelineOpen,
    openHubFlag,
    setOpenHubFlag,

    // Before
    openBeforePlain,
    openRoadmap,
    openBeforeGeneric,
    openBeforeChecklists,

    // During
    openEmergencyHub,

    // Weather
    weatherAlertSidebarOpen,
    weatherAlertForSidebar,
    openWeatherAlertSidebar,
    closeWeatherAlertSidebar,

    // Before highlights
    highlightChecklist,
    setHighlightChecklist,
    highlightRoadmap,
    setHighlightRoadmap,
    highlightQuickTips,
    setHighlightQuickTips,

    // Hub highlights
    highlightHubNavIndex,
    setHighlightHubNavIndex,

    // Scroll / state
    closePanels,
    trackNavigation,
    roadmapCenterRequested,
    setRoadmapCenterRequested,
    beforeScrollRequested,
    setBeforeScrollRequested,
    quickTipsCenterRequested,
    setQuickTipsCenterRequested,
    lastBeforeTarget,
    setLastBeforeTarget,

    duringScrollRequested,
    setDuringScrollRequested,

    // After
    highlightAfterTimeline,
    setHighlightAfterTimeline,
    highlightAfterTips,
    setHighlightAfterTips,
    highlightAfterChecklists,
    setHighlightAfterChecklists,
    lastAfterTarget,
    setLastAfterTarget,
    afterTargetPanelId,
    setAfterTargetPanelId,
    openAfterMentalHealth,
    openAfterTimeline,
    openAfterChecklists,
    openTimelinePanel,
    afterScrollRequested,
    setAfterScrollRequested,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigationContext() {
  return useContext(NavigationContext);
}
